import { isRunningInSEBBrowser, getUserToken } from '@amplify/secure-exam-browser';
import { datadogRum } from '@datadog/browser-rum';
import { isEmpty } from 'lodash';
import MLPServiceClient from '../clients/MLPServiceClient';
import AQUAServiceClient from '../clients/AQUAServiceClient';
import { STUDENT_ASSESSMENT, PORTAL_DASHBOARD } from '../../constants/url';
import keycloak, { keycloakInitOptions } from '../keycloak/keycloak';
import { isMClassUser, updateMasToken, isMigrationFailed } from '../keycloak/keycloakUtils';
import USER_TYPE from '../../constants/userType';
import { HTTP_METHODS } from '../../constants/http';
import selectors from '../../rootSelectors';
import { SEB_AUTH_URL } from '../../constants/secureBrowser';
import { isKioskChromeOS, removeKioskModeParameters, setLouisianaCookie } from "../utils/browserUtils";
import { clearState } from '../../AppStateActions';


export const SET_USER = 'SET_USER';

export const SET_USER_FROM_STORAGE = 'SET_USER_FROM_STORAGE';
export const AUTHENTICATION_FAILED = 'AUTHENTICATION_FAILED';

export const setUser = (userId, userName, userType, aquaId) => ({
    type: SET_USER,
    userId,
    userName,
    userType,
    aquaId,
});

export const authenticationFailed = (error) => ({
    type: AUTHENTICATION_FAILED,
    errorMessage: error.message,
});

const deleteSsoAuthTokenCookie = () => {
    document.cookie = `sso.auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const getKeycloakInitOptions = async () => {
    if (isKioskChromeOS() || isRunningInSEBBrowser()) {
        setLouisianaCookie();
    }
    if (isRunningInSEBBrowser()) {
        try {
            if (isEmpty(keycloak.tokenParsed)) {
                // eslint-disable-next-line camelcase
                const {keycloak_token, refresh_token} = await getUserToken(SEB_AUTH_URL);
                return {
                    ...keycloakInitOptions,
                    onLoad: 'check-sso',
                    token: keycloak_token,
                    refreshToken: refresh_token,
                };
            }
        } catch (error) {
            // Unable to get user tokens from SEB server
            datadogRum.addError(error);
            return keycloakInitOptions;
        }
    }
    return keycloakInitOptions;
};

export const resolveUser = () => (dispatch, getStore) => {
    const state = getStore();
    const isExternalURLSourcePresent = selectors.getExternalURLSourceStatus(state);
    return getKeycloakInitOptions()
        .then(initOptions => {
            return keycloak.init(initOptions).then(authenticated =>
                authenticated ? Promise.resolve() : keycloak.login());
        }).then(() => {
            if (keycloak.tokenParsed !== undefined) {
                if (isMigrationFailed()) {
                    // TODO: Add proper messaging
                    const migrationError = new Error('There is a problem with your account setup.');
                    dispatch(authenticationFailed(migrationError));
                    return Promise.reject(migrationError);
                }
                if (isMClassUser()) {
                // Logged in user is mClass user, call updateMasToken to refresh session
                    return updateMasToken();
                }
                // otherwise, cleanup possible leftovers from previously logged in mCLASS user
                // TODO: remove this statement when all back-ends ignore irrelevant auth token, or when it is not needed
                deleteSsoAuthTokenCookie();
            }
            return Promise.resolve();
        }).then(() => {
            const userType = keycloak.tokenParsed.realm_access.roles.includes(USER_TYPE.STUDENT) ? USER_TYPE.STUDENT : '';
            if(!isExternalURLSourcePresent) {
                const userId = isMClassUser() ? keycloak.tokenParsed.mclassUserSid : keycloak.tokenParsed.ampId;
                const persistedUserId = selectors.getPersistedUserId(state);
                if (persistedUserId !== undefined && persistedUserId !== userId) {
                // clear state if user has changed; having different users at this point means that page was reloaded
                // on non-landing URL, and redux state was loaded from session storage, along data of previous user
                    dispatch(clearState());
                }
                return dispatch(setUser(
                    userId,
                    keycloak.tokenParsed.name,
                    userType,
                ));
            }
            return dispatch(AQUAServiceClient.getAQUAUserId()).then(
                response => {
                    if (response) {
                        return dispatch(AQUAServiceClient.getAQUAUserDataById(response.UserId))
                            .then(userData => {
                                if (userData) {
                                    return dispatch(setUser(
                                        userData.UserExternalId,
                                        keycloak.tokenParsed.name,
                                        userType,
                                        userData.UserId,
                                    ));
                                }
                                return Promise.reject(userData);
                            });
                    }
                    return Promise.reject(response);
                });
        }).catch(error => {
            dispatch(authenticationFailed(error));
            return Promise.reject(error);
        });
};

export const logUserOut = () => {
    const keycloakLogoutOptions = {
        redirectUri: `${window.location.origin}${STUDENT_ASSESSMENT}`,
    };
    removeKioskModeParameters();
    if (process.env.NODE_ENV === 'development') {
        // custom log out for dev environment to avoid redirection to CI and handle CORS error
        return fetch(
            MLPServiceClient.LOGOUT,
            {
                method: HTTP_METHODS.GET,
                credentials: 'include',
            }
        ).finally(() => keycloak.logout(keycloakLogoutOptions));
    }
    if (datadogRum) {
        datadogRum.stopSessionReplayRecording();
    }
    if (isMClassUser()) {
        window.location = MLPServiceClient.LOGOUT;
        return Promise.resolve();
    }
    return keycloak.logout(keycloakLogoutOptions);
};

export const handleAuthenticationFailed = () => {
    logUserOut();
};

export const portalRedirect = () => {
    window.location = PORTAL_DASHBOARD;
};
